import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Play } from 'lucide-react';
import Navbar from '../components/Navbar';
import Button from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';

const SHOW_UNTIL_KEY = 'private_show_until';
const SHOW_VIDEO = `${process.env.PUBLIC_URL}/img/show-particular.mp4`;

export function grantPrivateShowAccess(hours = 24) {
  const until = Date.now() + hours * 60 * 60 * 1000;
  localStorage.setItem(SHOW_UNTIL_KEY, String(until));
  return until;
}

function hasPrivateShowAccess(isVip) {
  if (isVip) return true;
  const raw = localStorage.getItem(SHOW_UNTIL_KEY);
  if (!raw) return false;
  const until = Number(raw);
  return Number.isFinite(until) && until > Date.now();
}

const PrivateShow = () => {
  const { user, isLoggedIn, loading } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const allowed = isLoggedIn && (user?.isVip || hasPrivateShowAccess(user?.isVip));

  useEffect(() => {
    if (!user?.isVip) return;
    const raw = localStorage.getItem(SHOW_UNTIL_KEY);
    if (!raw || Number(raw) <= Date.now()) {
      grantPrivateShowAccess(24);
    }
  }, [user?.isVip]);

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (video.paused) {
        await video.play();
        setPlaying(true);
      } else {
        video.pause();
        setPlaying(false);
      }
    } catch {
      setPlaying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Loader2 className="h-10 w-10 animate-spin text-rose-500" />
      </div>
    );
  }

  if (!isLoggedIn) {
    navigate('/login', { replace: true });
    return null;
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="mx-auto max-w-lg px-4 pb-10 pt-24 text-center">
          <h1 className="text-xl font-semibold text-white">Show particular</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Você ainda não tem acesso. Compre o show na página inicial para liberar 24h.
          </p>
          <Button className="mt-6" onClick={() => navigate('/app')}>
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-2xl px-4 pb-10 pt-20">
        <button
          type="button"
          onClick={() => navigate('/app')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl">
          <div className="relative aspect-[9/16] max-h-[70vh] w-full bg-zinc-900 sm:aspect-video sm:max-h-none">
            <video
              ref={videoRef}
              key={SHOW_VIDEO}
              className="h-full w-full cursor-pointer object-cover"
              src={SHOW_VIDEO}
              playsInline
              loop
              poster={`${process.env.PUBLIC_URL}/img/1.jpeg`}
              onClick={togglePlay}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
            />

            {!playing && (
              <button
                type="button"
                onClick={togglePlay}
                aria-label="Reproduzir"
                className="absolute inset-0 z-10 flex items-center justify-center bg-black/25 transition-opacity hover:bg-black/35"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-600 text-white shadow-lg ring-4 ring-white/20">
                  <Play className="ml-1 h-8 w-8 fill-current" />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateShow;
